/* eslint-disable func-style, no-cond-assign, no-console */
import * as easings from './easings';
const MINUS_ONE = -1;
export function arrayValueIsInArray(arrayValues, arrayMatchValues) {
  return arrayValues.filter((element) => arrayMatchValues.indexOf(element) > MINUS_ONE).length > 0;
}

export function wrap(element, className) {
  const wrapper = document.createElement('div');
  if (className) {
    wrapper.className = className;
  }
  element.parentNode.insertBefore(wrapper, element);
  wrapper.appendChild(element);
}

export function unwrap(element) {
  const parent = element.parentNode;
  const grandParent = parent.parentNode;
  grandParent.insertBefore(element, parent);
  grandParent.removeChild(parent);
}

/**
 * Returns the width of the specified DOM element.
 * @param {Element} elem The DOM element that we want the width of.
 * @return {Number} The width of the specified DOM element.
 */
export function getElementWidth(elem) {
  return elem.getBoundingClientRect().width;
}

export function getWidthOfWindow() {
  const actualWidth = window.innerWidth ||
                    document.documentElement.clientWidth ||
                    document.body.clientWidth ||
                    document.body.offsetWidth;
  return actualWidth;
}

export function getHeightOfWindow() {
  const actualWidth = window.innerHeight ||
                    document.documentElement.clientHeight ||
                    document.body.clientHeight ||
                    document.body.offsetHeight;
  return actualWidth;
}

export function offset(element) {
  let offsetLeft = 0;
  let offsetTop = 0;
  if (element.offsetParent) {
    do {
      offsetLeft += element.offsetLeft;
      offsetTop += element.offsetTop;
    } while (element = element.offsetParent);
  }
  return {
    left: offsetLeft,
    top: offsetTop,
  };
}

// Position of left edge relative to frame left courtesy
// http://www.quirksmode.org/js/findpos.html
function absleft(element) {
  let x = 0;
  for (; element; element = element.offsetParent) {
    x += element.offsetLeft;
  }
  return x;
}

// Position of top edge relative to top of frame.
function abstop(element) {
  let y = 0;
  for (; element; element = element.offsetParent) {
    y += element.offsetTop;
  }
  return y;
}

// True if element's bounding rectangle includes a non-zero area
// the container's bounding rectangle.
export function isOverflowed(element, container) {
  const cont = container || element.offsetParent;
  const left = absleft(element);
  const right = left + element.offsetWidth;
  const top = abstop(element);
  const bottom = top + element.offsetHeight;
  const cleft = absleft(cont);
  const cright = cleft + cont.offsetWidth;
  const ctop = abstop(cont);
  const cbottom = ctop + cont.offsetHeight;
  return left < cleft || top < ctop || right > cright || bottom > cbottom;
}

export function hasDirectDescendant(parent, child) {
  if (parent.hasChildNodes()) {
    const children = parent.childNodes;
    for (let i = 0; i < children.length; i++) {
      if (children[i].isEqualNode(child)) {
        return true;
      }
    }
  }
  return false;
}

export function classListContains(cssClassList, cssClassMatches) {
  let thisContains = false;
  for (const cssClass of cssClassMatches) {
    if (!thisContains) {
      thisContains = cssClassList.contains(cssClass);
    }
  }
  return thisContains;
}

export function getParentWithSelector(element, selectors) {
  if (!element.parentNode) {
    return false;
  } else if (element.parentNode.classList && classListContains(element.parentNode.classList, selectors)) {
    return element.parentNode;
  }
  return getParentWithSelector(element.parentNode, selectors);
}

/**
 * Checks the specified DOM element for the presence of the specified CSS class.
 * @param {Element} elem The DOM element on which we are checking for the presence of the CSS class.
 * @param {String} cssClass The CSS class to check for on the specified element.
 * @return {boolean}
 */
export function hasClass(elem, cssClass) {
  return elem.classList.contains(cssClass.trim());
}

/**
 * Adds the specified CSS class to the specified DOM element.
 * @param {Element} elem The DOM element to add the specified CSS class to.
 * @param {String} cssClass The CSS class to be added.
 * @return void
 */
export function addClass(elem, cssClass) {
  elem.classList.add(cssClass.trim());
}

/**
 * Removes the specified CSS class from the specified DOM element.
 * @param {Element} elem The DOM element to remove from the specified CSS class.
 * @param {String} cssClass The CSS class to be removed.
 * @return void
 */
export function removeClass(elem, cssClass) {
  elem.classList.remove(cssClass.trim());
}

/**
 * As Internet Explorer does not support adding of multiple classes to an element via Element.classList.add()
 * this utility method provides the ability to add multiple classes in one operation.
 * @param elem
 * @param classes
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/classList
 * @see http://caniuse.com/#search=classList
 */
export function addClasses(elem, classes) {
  for (const cls of classes) {
    addClass(elem, cls);
  }
}

/**
 * As Internet Explorer does not support removing of multiple classes from an element via Element.classList.remove()
 * this utility method provides the ability to remove multiple classes in one operation.
 * @param elem
 * @param classes
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/classList
 * @see http://caniuse.com/#search=classList
 */
export function removeClasses(elem, classes) {
  for (const cls of classes) {
    removeClass(elem, cls);
  }
}

/**
 * Utility method for applying multiple inline styles to an element in one operation.
 * @param {Element} element The applied being styled.
 * @param {Object} styles An object defining the styles to be applied.
 */
export function setStyles(element, styles) {
  Object.keys(styles).forEach((key) => {
    if (key === 'transform') {
      element.style[key] = `translateX(${ styles[key] }%)`;
    }
    element.style[key] = styles[key];
  });
}

/**
 * Clears all inline styles from the specified DOM element.
 * @param {Element} element The DOM element on which the inline styles are to be removed.
 */
export function clearStyles(element) {
  element.style.cssText = '';
}

/**
 * Provides the ability to animate the scrolling of an overflowed element either vertically or horizontally.
 * @param element
 * @param options
 */
export function smoothScroll(element, options) {
  const milliseconds = 1000;
  const seconds = 60;
  const requestAnimFrame = (function requestAnimFrame() {
    return window.requestAnimationFrame ||
      window.webkitRequestAnimationFrame ||
      window.mozRequestAnimationFrame ||
      function reqAF(cbk) {
        window.setTimeout(cbk, milliseconds / seconds);
      };
  }());
  const defaults = {
    to: 0,
    duration: 250,
    axis: 'horizontal',
    easing: 'easeInOutQuad',
  };
  const settings = Object.assign({}, defaults, options);
  const direction = settings.axis === 'horizontal' ? 'scrollLeft' : 'scrollTop';
  const start = element[direction];
  const change = settings.to - start;
  const animationStart = Number(new Date());
  let animating = true;
  const animateScroll = function animateScroll() {
    if (!animating) {
      if (settings.cbk) {
        settings.cbk();
      }
      return;
    }
    requestAnimFrame(animateScroll);
    const now = Number(new Date());
    element[direction] = Math.floor(easings[settings.easing](now - animationStart, start, change, settings.duration));

    if (now > animationStart + settings.duration) {
      element[direction] = settings.to;
      animating = false;
    }
  };
  requestAnimFrame(animateScroll);
}

export function htmlFromString(html) {
  const div = document.createElement('div');
  div.innerHTML = html.trim();

  // Change this to div.childNodes to support multiple top-level nodes
  return div.firstChild;
}

export function createElement(elementType, targetElement) {
  const element = document.createElement(elementType);
  targetElement.appendChild(element);
  return element;
}

export function removeElement(element) {
  const parent = element.parentElement;
  parent.removeChild(element);
}

export function createElementBefore(elementType, targetElement, beforeElement) {
  const element = document.createElement(elementType);
  targetElement.insertBefore(element, beforeElement);
  return element;
}

export function fadeOut(element) {
  const OPACITY_INCREMENT = 0.05;
  (function fade() {
    if ((element.style.opacity -= OPACITY_INCREMENT) <= 0) {
      element.style.display = 'none';
      element.style.opacity = 0;
    } else {
      requestAnimationFrame(fade);
    }
  }());
}

export function fadeIn(element, display) {
  const OPACITY_INCREMENT = 0.05;
  element.style.display = display || 'block';
  (function fade() {
    let val = parseFloat(element.style.opacity);
    if (!((val += OPACITY_INCREMENT) > 1)) {
      element.style.opacity = val;
      requestAnimationFrame(fade);
    }
  }());
}

export function filterByTagName(htmlCollection, tagname) {
  const filtered = [];
  tagname = tagname.toLowerCase();

  for (let i = 0; i < htmlCollection.length; i++) {
    if (htmlCollection.item(i).tagName.toLowerCase() === tagname) {
      filtered.push(htmlCollection.item(i));
    }
  }
  return filtered;
}



// WEBPACK FOOTER //
// ./js/utils/html.js